import type { Dance, PlaybackMode } from "@recital-master/shared";
import { dancesForDancers } from "@recital-master/shared";

/** Recital editorial content belongs only to the complete-program intent. */
export function withRecitalIntro(
  dances: Dance[],
  intro: Dance | null | undefined,
  mode: PlaybackMode
): Dance[] {
  return mode === "full-recital" && intro ? [intro, ...dances] : dances;
}

/**
 * Builds the ordered dance queue for a playback request. The queue is
 * always a list of dance clips (never timestamps into one long video) —
 * full recital, from-here-onward, one dance, or a My Dancer filter.
 */
export function buildQueue(
  dances: Dance[],
  mode: PlaybackMode,
  options: { startDanceId?: string; dancerIds?: string[] } = {}
): { queue: Dance[]; startIndex: number } {
  switch (mode) {
    case "full-performance":
    case "full-recital": {
      const index = options.startDanceId
        ? dances.findIndex((dance) => dance.danceId === options.startDanceId)
        : 0;
      return { queue: dances, startIndex: Math.max(0, index) };
    }
    case "from-dance": {
      const index = dances.findIndex((dance) => dance.danceId === options.startDanceId);
      // Keep the full program in the queue so "previous" can reach earlier
      // dances; just start at the selected one.
      return { queue: dances, startIndex: Math.max(0, index) };
    }
    case "single-dance": {
      const dance = dances.find((entry) => entry.danceId === options.startDanceId);
      return { queue: dance ? [dance] : [], startIndex: 0 };
    }
    case "my-dancer": {
      const queue = dancesForDancers(dances, options.dancerIds ?? []);
      const index = options.startDanceId
        ? queue.findIndex((dance) => dance.danceId === options.startDanceId)
        : 0;
      return { queue, startIndex: Math.max(0, index) };
    }
  }
}
